import React, { Suspense, lazy, useEffect } from 'react'
import Table from 'react-bootstrap/Table'
import FormCheck from 'react-bootstrap/FormCheck'
import { MdFlight } from 'react-icons/md'
import { useLazyQuery } from '@apollo/client'

import { Booking, BookingAddOn, GuestShort } from '../../../types'
import { useCurrentUser } from '../../../common'
import { SimpleLoader } from '../../../components'
import { salmon } from '../../../styles/themeColors'
import { addonMetadata } from '../../../common/constants'
import { ChildrenIcon } from '../../../components/customIcons'
import { GET_GUEST_GROUP } from '../../../graphql'

import { orderBookingGuests } from './bookingFunctions'

const AddFlightInfo = lazy(() => import('./AddFlightInfo'))

export const BookingGuestsTable: React.FC<{
  guests?: BookingAddOn[]
  title?: string
  titleClassName?: string
  booking?: Booking
  hideEmail?: boolean
  hidePlusOne?: boolean
  className?: string
  hideTitle?: boolean
  hideFlightInfo?: boolean
}> = ({
  title = 'Guests Information',
  titleClassName = 'mb-2 mt-3 mt-lg-5 d-flex align-items-center',
  booking,
  guests = [],
  hideEmail = false,
  hidePlusOne = false,
  className = '',
  hideTitle = false,
  hideFlightInfo = false,
}) => {
  const { email } = useCurrentUser()
  const [getGroup, { data: groupData, loading: loadingGroup }] = useLazyQuery(
    GET_GUEST_GROUP,
    {
      fetchPolicy: 'cache-and-network',
    }
  )
  const orderedGuests = orderBookingGuests({
    guests: guests.filter(el => el.guest),
    currentUserEmail: email,
  })

  useEffect(() => {
    if (hideFlightInfo) return
    if (guests.length <= 0 || orderedGuests?.length <= 0) return
    if (loadingGroup) return
    if (groupData) return

    const guest = orderedGuests[0].guest as GuestShort
    getGroup({ variables: { eventId: guest?.eventId } })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests.length])

  if (guests.length <= 0) return null

  const group: GuestShort[] = groupData?.me?.myGroup
  const currentUserIsLeader = group?.find(
    el => el.email === email && el.group?.primaryContact
  )

  return (
    <>
      {!hideTitle && (
        <div className={titleClassName}>
          <p className="fz-18 fwsb m-0 pr-3">{title}</p>
        </div>
      )}
      <Table borderless striped className={className}>
        <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th className="px-0">{/* icon */}</th>
            {!hideEmail && <th>Email</th>}
            <th>Phone</th>
            {!hidePlusOne && <th>+1</th>}
            {!hideFlightInfo && <th>Flight Info</th>}
          </tr>
        </thead>
        <tbody>
          {orderedGuests.map(addon => {
            const { id, offeringAddOn } = addon
            const guest = addon.guest as GuestShort
            const { kid } = addonMetadata
            const canEditFlight = currentUserIsLeader || guest?.email === email

            return (
              <tr key={`row-${id}`}>
                <td>{guest?.firstName}</td>
                <td>{guest?.lastName}</td>
                <td className="px-0">
                  {offeringAddOn.metadata?.subtype === kid && (
                    <ChildrenIcon color={salmon} />
                  )}
                </td>
                {!hideEmail && <td>{guest?.email}</td>}
                <td>{guest?.phone}</td>
                {!hidePlusOne && (
                  <td>
                    {guest?.isPlusOne && (
                      <FormCheck checked custom type="checkbox" readOnly />
                    )}
                  </td>
                )}
                {!hideFlightInfo && canEditFlight && guest && booking && (
                  <td>
                    <Suspense fallback={<SimpleLoader />}>
                      <AddFlightInfo
                        booking={booking}
                        guestProfile={guest}
                        ctaClassName="rounded-pill py-0 px-2"
                        customText={<MdFlight size={24} color="white" />}
                        refetchQueries={['getBookingInfo']}
                      />
                    </Suspense>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </>
  )
}
